import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  ConversationTranscript,
  TranscriptMessage,
} from '@/components/leads/ConversationTranscript';
import { LeadStatusPill } from '@/components/leads/LeadStatusPill';
import { LeadProvenanceCard } from '@/components/leads/LeadProvenanceCard';
import { LeadCustomRecords } from '@/components/LeadCustomRecords';
import { CollectedFieldsTable } from '@/components/leads/CollectedFieldsTable';
import { ActivityTimeline } from '@/components/ActivityTimeline';
import {
  leadsService,
  Lead,
  LeadDetail as LeadDetailType,
  LeadStatus,
  LeadEvent,
} from '@/services/leads.service';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, User, StickyNote, Send, UserCheck, Clock, ClipboardList, Bot, Play, Pause, Zap, ChevronDown,
  Trophy, AlertCircle, Mic, Trash2, Briefcase, Banknote, AlarmClock,
} from 'lucide-react';
import { departmentsService, Department } from '@/services/departments.service';
import { settingsService, TeamUser } from '@/services/settings.service';
import { dealsService, Deal } from '@/services/deals.service';
import { DateTimePicker } from '@/components/DateTimePicker';
import { VoiceRecorder } from '@/components/voice/VoiceRecorder';
import { uploadsService } from '@/services/uploads.service';
import { voiceService } from '@/services/voice.service';
import toast from 'react-hot-toast';
import { formatDistanceToNow, format } from 'date-fns';
import clsx from 'clsx';
import { useLabels, useCompanyCurrency, useCurrencyField } from '@/contexts/WorkspaceContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initialsFor(name?: string, phone?: string) {
  if (name?.trim()) {
    return name.trim().split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
  }
  return phone?.slice(-2) ?? '??';
}

function relativeTime(ts?: string) {
  if (!ts) return '—';
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ts; }
}

function fullDateTime(ts?: string) {
  if (!ts) return '—';
  try { return format(new Date(ts), 'MMM d, yyyy HH:mm'); } catch { return ts; }
}

/** ISO timestamp → local 'YYYY-MM-DDTHH:mm' for the DateTimePicker. */
function toLocalDateTimeValue(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const LEAD_STATUSES: LeadStatus[] = ['new', 'contacted', 'qualified', 'won', 'lost', 'spam'];

// ─── Mark-as-Won/Lost dialog ──────────────────────────────────────────────────

const COMMON_CURRENCIES = ['AED', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'PKR'];
/** The company's currency first, then the usual ones. */
const currencyChoices = (company: string) => [company, ...COMMON_CURRENCIES.filter((c) => c !== company)];

interface WonDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (amount: number, currency: string, reason: string) => Promise<void>;
  defaultCurrency?: string;
}

function WonDialog({ open, onClose, onConfirm, defaultCurrency }: WonDialogProps) {
  const companyCurrency = useCompanyCurrency();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useCurrencyField(defaultCurrency);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm(parseFloat(amount) || 0, currency, reason);
      setAmount(''); setReason('');
      // Confetti burst — respects prefers-reduced-motion via matchMedia
      const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (motionOk) {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { x: 0.5, y: 0.55 },
          colors: ['#E5C77D', '#C8A55B', '#9C7E3F', '#F4F1EB', '#4F7B36'],
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Mark as won">
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl border border-success/30 bg-success-soft p-3.5">
          <Trophy size={16} className="shrink-0 text-success" />
          <p className="text-sm text-success font-medium">Great work! Capture the deal details below.</p>
        </div>

        <div className="flex gap-3">
          <div className="w-28">
            <label className="mb-1 block text-xs font-medium text-text-secondary">Currency</label>
            <Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {currencyChoices(companyCurrency).map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              Deal amount <span className="text-text-muted">(optional)</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={clsx(
                'w-full rounded-xl border border-border bg-surface-alt px-3.5 py-2.5 text-sm text-text-primary',
                'placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
              )}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            Reason / note <span className="text-text-muted">(optional)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Signed 3-month contract"
            rows={2}
            className={clsx(
              'w-full resize-none rounded-xl border border-border bg-surface-alt px-3.5 py-2.5 text-sm text-text-primary',
              'placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
            )}
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={saving} onClick={handleConfirm}>
            Mark as won
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface LostDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

function LostDialog({ open, onClose, onConfirm }: LostDialogProps) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm(reason);
      setReason('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Mark as lost">
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl border border-danger/30 bg-danger-soft p-3.5">
          <AlertCircle size={16} className="shrink-0 text-danger" />
          <p className="text-sm text-danger font-medium">What was the reason for losing this lead?</p>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            Reason <span className="text-text-muted">(optional)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Out of budget, Chose competitor, No longer interested…"
            rows={3}
            className={clsx(
              'w-full resize-none rounded-xl border border-border bg-surface-alt px-3.5 py-2.5 text-sm text-text-primary',
              'placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
            )}
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {['Out of budget', 'Chose competitor', 'No longer interested', 'No response'].map((s) => (
              <button
                key={s}
                onClick={() => setReason(s)}
                className="rounded-full border border-border bg-surface px-2.5 py-0.5 text-[11px] text-text-secondary hover:bg-surface-alt transition"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="danger" size="sm" loading={saving} onClick={handleConfirm}>
            Mark as lost
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Create-deal dialog ───────────────────────────────────────────────────────

interface CreateDealDialogProps {
  open: boolean;
  onClose: () => void;
  lead: Lead;
  onCreated: (deal: Deal) => void;
}

function CreateDealDialog({ open, onClose, lead, onCreated }: CreateDealDialogProps) {
  const companyCurrency = useCompanyCurrency();
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useCurrencyField();
  const [ownerUserUid, setOwnerUserUid] = useState('');
  const [team, setTeam] = useState<TeamUser[]>([]);
  const [saving, setSaving] = useState(false);

  // Pre-fill from the lead every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setTitle(lead.profileName || lead.phone || lead.email || 'New lead');
    setAmount(lead.estimatedValueAmount != null ? String(lead.estimatedValueAmount) : '');
    setCurrency(lead.estimatedValueCurrency ?? companyCurrency);
    setOwnerUserUid(lead.assignedToUserUid ?? '');
    settingsService.listTeam().then(setTeam).catch(() => setTeam([]));
  }, [open, lead]);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const deal = await dealsService.create({
        title: title.trim(),
        amount: amount ? parseFloat(amount) || undefined : undefined,
        currency,
        leadUid: lead.leadUid,
        contactUid: lead.contactUid || undefined,
        ownerUserUid: ownerUserUid || undefined,
      });
      // Log on the lead timeline via the existing notes endpoint (best-effort).
      leadsService.addNote(lead.leadUid, `Deal created: ${deal.title}`).catch(() => undefined);
      // Plain <a> — react-hot-toast renders outside the Router, so <Link> would throw.
      toast.success(
        <span>
          Deal created —{' '}
          <a href="/deals" className="font-semibold underline">View deals</a>
        </span>,
      );
      onCreated(deal);
      onClose();
    } catch (e) {
      if (!(e as { response?: unknown })?.response) toast.error('Failed to create deal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create deal from lead">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Deal title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={clsx(
              'w-full rounded-xl border border-border bg-surface-alt px-3.5 py-2.5 text-sm text-text-primary',
              'placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
            )}
          />
        </div>

        <div className="flex gap-3">
          <div className="w-28">
            <label className="mb-1 block text-xs font-medium text-text-secondary">Currency</label>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencyChoices(companyCurrency).map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              Amount <span className="text-text-muted">(optional)</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={clsx(
                'w-full rounded-xl border border-border bg-surface-alt px-3.5 py-2.5 text-sm text-text-primary',
                'placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
              )}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Owner</label>
          <Select value={ownerUserUid} onChange={(e) => setOwnerUserUid(e.target.value)}>
            <option value="">Me (current user)</option>
            {team.filter((u) => u.status === 'active').map((u) => (
              <option key={u.userUid} value={u.userUid}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={saving} disabled={!title.trim()} onClick={submit}>
            Create deal
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Build transcript ─────────────────────────────────────────────────────────

const memberName = (u: TeamUser) => `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;

function buildTranscript(detail: LeadDetailType, team: TeamUser[] = []): TranscriptMessage[] {
  const msgs: TranscriptMessage[] = [];

  for (const ev of detail.leadEvents) {
    if (ev.type === 'inbound_message') {
      msgs.push({
        id: ev.eventUid,
        role: 'inbound',
        body: (ev.payload.body as string) ?? undefined,
        mediaType: (ev.payload.mediaType as string) ?? undefined,
        mediaUrl: ev.payload.mediaUrl as string | undefined,
        mediaFilename: ev.payload.mediaFilename as string | undefined,
        mediaStorageStatus: ev.payload.mediaStorageStatus as string | undefined,
        location: ev.payload.location as TranscriptMessage['location'],
        flowResponse: ev.payload.flowResponse as TranscriptMessage['flowResponse'],
        audioUrl: (ev.payload.audioUrl as string) ?? undefined,
        audioMime: (ev.payload.audioMime as string) ?? undefined,
        audioDurationSec: (ev.payload.audioDurationSec as number) ?? undefined,
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'outbound_message') {
      msgs.push({
        id: ev.eventUid,
        role: 'outbound',
        body: (ev.payload.body as string) ?? undefined,
        mediaType: (ev.payload.mediaType as string) ?? undefined,
        mediaUrl: (ev.payload.mediaUrl as string) ?? undefined,
        footer: (ev.payload.footer as string) ?? undefined,
        buttons: (ev.payload.buttons as { type: string; text?: string }[]) ?? undefined,
        audioUrl: (ev.payload.audioUrl as string) ?? undefined,
        audioMime: (ev.payload.audioMime as string) ?? undefined,
        timestamp: ev.createdAt,
        isBot: ev.actorKind === 'bot' || ev.actorKind === 'ai',
        deliveryStatus: ev.payload.deliveryStatus as TranscriptMessage['deliveryStatus'],
        failureReason: ev.payload.failureReason as string | undefined,
        source: ev.payload.source as string | undefined,
      });
    } else if (ev.type === 'note_added') {
      msgs.push({
        id: ev.eventUid,
        role: 'system',
        body: `Note: ${(ev.payload.body as string) ?? ''}`,
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'status_changed') {
      msgs.push({
        id: ev.eventUid,
        role: 'system',
        body: `Status changed: ${ev.payload.from} → ${ev.payload.to}`,
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'assigned') {
      const to = ev.payload.to as string | undefined;
      const member = to ? team.find((u) => u.userUid === to) : undefined;
      msgs.push({
        id: ev.eventUid,
        role: 'system',
        body: to ? `🔁 Assigned to ${member ? memberName(member) : 'a team member'}` : '🔁 Assignment updated',
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'handoff_to_human') {
      msgs.push({
        id: ev.eventUid,
        role: 'system',
        body: '🤝 Handed off to human rep',
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'lead_ad_form_submitted') {
      const formName = ev.payload.formName as string | undefined;
      const kind = ev.payload.provider === 'google' ? 'Google lead form' : ev.payload.provider === 'tiktok' ? 'TikTok lead form' : 'Lead Ad form';
      msgs.push({
        id: ev.eventUid,
        role: 'system',
        body: formName ? `${kind} submitted: "${formName}"` : `${kind} submitted`,
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'bot_paused_by_user') {
      msgs.push({
        id: ev.eventUid,
        role: 'system',
        body: '⏸ Bot paused — manual mode',
        timestamp: ev.createdAt,
      });
    } else if (ev.type === 'flow_started') {
      const flowName = (ev.payload.flowName as string) ?? 'Bot flow';
      const trig = ev.payload.trigger as Record<string, unknown> | undefined;
      const channel = trig?.channel as string | undefined;
      const triggerType = trig?.type as string | undefined;
      const matchedKeyword = trig?.matchedKeyword as string | undefined;
      const adId = trig?.adId as string | undefined;
      const campaignId = trig?.campaignId as string | undefined;
      const formId = trig?.formId as string | undefined;
      const channelLabel: Record<string, string> = {
        facebook_ads:     '📘 Facebook ad',
        instagram_ads:    '📷 Instagram ad',
        meta_ads:         '🅵 Meta ad',
        meta_lead_form:   '📋 Meta lead form',
        tiktok_lead_form: '🎵 TikTok lead form',
        google_lead_form: '🔎 Google lead form',
        google_ads:       '🔎 Google ad',
        web_form:         '🌐 Web form',
        email:            '✉️ Email',
        whatsapp_inbound: '💬 WhatsApp inbound',
        manual:           '✋ Manual',
      };
      const channelStr = channel ? (channelLabel[channel] ?? channel) : '';
      const detailParts: string[] = [];
      if (triggerType === 'keyword' && matchedKeyword) detailParts.push(`keyword "${matchedKeyword}"`);
      if (triggerType === 'ad' && adId) detailParts.push(`ad ${adId}`);
      if (triggerType === 'ad' && !adId && campaignId) detailParts.push(`campaign ${campaignId}`);
      if (triggerType === 'lead_ad_form' && formId) detailParts.push(`form ${formId}`);
      const detailStr = detailParts.length ? ` · ${detailParts.join(', ')}` : '';
      msgs.push({
        id: ev.eventUid,
        role: 'system',
        body: `🤖 Bot started: ${flowName}${channelStr ? ` (${channelStr})` : ''}${detailStr}`,
        timestamp: ev.createdAt,
      });
    }
  }

  msgs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  return msgs;
}

// ─── Note row ─────────────────────────────────────────────────────────────────

function NoteRow({ event }: { event: LeadEvent }) {
  return (
    <div className="flex gap-2.5 py-2.5 border-b border-border last:border-0">
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-alt text-text-muted">
        <StickyNote size={11} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-text-primary leading-snug whitespace-pre-wrap break-words">
          {(event.payload.body as string) ?? ''}
        </p>
        <p className="mt-1 text-[10px] text-text-muted">{fullDateTime(event.createdAt)}</p>
      </div>
    </div>
  );
}

// ─── Assignment banner ────────────────────────────────────────────────────────

function AssignmentBanner({
  lead,
  onReassign,
}: {
  lead: Lead;
  onReassign: (uid: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [savingUid, setSavingUid] = useState<string | null>(null);
  const [team, setTeam] = useState<TeamUser[] | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Load team once. Used both for the dropdown and to resolve the current
  // assignee's name (the lead only stores `assignedToUserUid`).
  useEffect(() => {
    settingsService.listTeam()
      .then((users) => setTeam(users))
      .catch(() => setTeamError('Could not load team'));
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handlePick = async (userUid: string) => {
    if (userUid === lead.assignedToUserUid) {
      setOpen(false);
      return;
    }
    setSavingUid(userUid);
    try {
      await onReassign(userUid);
      setOpen(false);
      setQuery('');
    } finally {
      setSavingUid(null);
    }
  };

  const currentAssignee = team?.find((u) => u.userUid === lead.assignedToUserUid);
  const assigneeDisplay = currentAssignee
    ? `${currentAssignee.firstName} ${currentAssignee.lastName}`.trim()
    : lead.assignedToUserUid
      ? 'Loading…'
      : 'Unassigned';
  const assigneeInitials = currentAssignee
    ? `${currentAssignee.firstName.charAt(0)}${currentAssignee.lastName.charAt(0)}`.toUpperCase()
    : lead.assignedToUserUid
      ? '··'
      : '??';

  const q = query.trim().toLowerCase();
  const visibleUsers = (team ?? [])
    .filter((u) => u.status === 'active')
    .filter((u) => {
      if (!q) return true;
      const name = `${u.firstName} ${u.lastName}`.toLowerCase();
      return name.includes(q) || u.email.toLowerCase().includes(q);
    });

  return (
    <div className="mx-6 mt-4 mb-0 rounded-xl border border-border bg-surface-alt/60 px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary text-[10px] font-bold">
          {assigneeInitials}
        </div>
        <div className="min-w-0">
          <p className="text-[11.5px] text-text-muted leading-tight">Assigned to</p>
          <p className="text-[12.5px] font-semibold text-text-primary truncate">
            {assigneeDisplay}
          </p>
        </div>
      </div>

      <div className="relative" ref={popoverRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          Reassign <ChevronDown size={12} />
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-1.5 z-20 w-72 rounded-xl border border-border bg-surface shadow-elevated">
            <div className="p-2.5 border-b border-border">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search team by name or email…"
                className={clsx(
                  'w-full rounded-lg border border-border bg-surface-alt px-2.5 py-1.5 text-[12.5px] text-text-primary',
                  'placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary-soft',
                )}
              />
            </div>

            <div className="max-h-64 overflow-y-auto py-1">
              {team === null && !teamError && (
                <div className="px-3 py-3 text-[12px] text-text-muted">Loading team…</div>
              )}
              {teamError && (
                <div className="px-3 py-3 text-[12px] text-danger">{teamError}</div>
              )}
              {team !== null && !teamError && visibleUsers.length === 0 && (
                <div className="px-3 py-4 text-center">
                  <p className="text-[12px] text-text-muted">
                    {q ? 'No matching team members.' : 'No active team members.'}
                  </p>
                  {!q && (
                    <Link
                      to="/settings/team"
                      onClick={() => setOpen(false)}
                      className="mt-1 inline-block text-[11.5px] font-semibold text-primary hover:underline"
                    >
                      Add team members →
                    </Link>
                  )}
                </div>
              )}
              {visibleUsers.map((u) => {
                const isCurrent = u.userUid === lead.assignedToUserUid;
                const isSaving = savingUid === u.userUid;
                return (
                  <button
                    key={u.userUid}
                    onClick={() => handlePick(u.userUid)}
                    disabled={savingUid !== null}
                    className={clsx(
                      'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
                      'hover:bg-surface-alt disabled:opacity-60',
                      isCurrent && 'bg-primary-soft/40',
                    )}
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary text-[10px] font-bold">
                      {`${u.firstName.charAt(0)}${u.lastName.charAt(0)}`.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-semibold text-text-primary truncate">
                        {u.firstName} {u.lastName}
                        {isCurrent && (
                          <span className="ml-1.5 text-[10px] font-normal text-text-muted">(current)</span>
                        )}
                      </p>
                      <p className="text-[11px] text-text-muted truncate">{u.email}</p>
                    </div>
                    {isSaving && (
                      <span className="text-[11px] text-text-muted shrink-0">Saving…</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LeadDetail() {
  const companyCurrency = useCompanyCurrency();
  const t = useLabels();
  const { has: hasModule } = useModuleAccess();
  const { leadUid } = useParams<{ leadUid: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<LeadDetailType | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);

  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  const [botActionLoading, setBotActionLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [handoverOpen, setHandoverOpen] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [handoverDeptUid, setHandoverDeptUid] = useState('');
  const [handoverReason, setHandoverReason] = useState('');
  const [handingOver, setHandingOver] = useState(false);

  useEffect(() => {
    departmentsService.list().then(setDepartments).catch(() => setDepartments([]));
  }, []);

  const handleHandover = async () => {
    if (!leadUid || !handoverDeptUid) return;
    setHandingOver(true);
    try {
      await leadsService.handoverToDepartment(leadUid, {
        deptUid: handoverDeptUid,
        reason: handoverReason.trim() || undefined,
      });
      toast.success('Handed over to department');
      setHandoverOpen(false);
      setHandoverDeptUid('');
      setHandoverReason('');
      await load();
    } catch {
      toast.error('Handover failed');
    } finally {
      setHandingOver(false);
    }
  };

  const handleDelete = async () => {
    if (!leadUid) return;
    setDeleting(true);
    try {
      await leadsService.remove(leadUid);
      toast.success('Lead deleted');
      navigate('/leads');
    } catch {
      toast.error('Failed to delete lead');
      setDeleting(false);
    }
  };

  // Mark-as-won / lost dialogs
  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);

  // Lead → deal conversion
  const [dealDialogOpen, setDealDialogOpen] = useState(false);
  const [existingDeal, setExistingDeal] = useState<Deal | null>(null);

  // If a deal was already created from this lead, offer "View deal" instead
  // of letting the user create duplicates.
  useEffect(() => {
    if (!leadUid) return;
    dealsService.list({ leadUid, limit: 1 })
      .then((r) => setExistingDeal(r.items[0] ?? null))
      .catch(() => setExistingDeal(null));
  }, [leadUid]);

  // Follow-up reminder. Draft + explicit save — the DateTimePicker fires
  // onChange for every intermediate pick (date, then hour, then minute), so
  // saving on change would PATCH and toast several times per scheduling.
  const [followUpDraft, setFollowUpDraft] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  useEffect(() => {
    setFollowUpDraft(toLocalDateTimeValue(lead?.nextFollowUpAt));
  }, [lead?.nextFollowUpAt]);
  const followUpDirty = followUpDraft !== toLocalDateTimeValue(lead?.nextFollowUpAt);

  const handleFollowUpSave = async () => {
    if (!leadUid) return;
    setSavingFollowUp(true);
    try {
      const updated = await leadsService.update(leadUid, {
        nextFollowUpAt: followUpDraft ? new Date(followUpDraft).toISOString() : null,
      });
      setLead(updated);
      toast.success(followUpDraft ? 'Follow-up scheduled' : 'Follow-up cleared');
    } catch (e) {
      if (!(e as { response?: unknown })?.response) toast.error('Failed to update follow-up');
    } finally {
      setSavingFollowUp(false);
    }
  };

  const replyRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [team, setTeam] = useState<TeamUser[]>([]);
  const teamRef = useRef<TeamUser[] | null>(null);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [sendingVoice, setSendingVoice] = useState(false);

  const load = async () => {
    if (!leadUid) return;
    setLoading(true);
    try {
      // Events store user IDs; the team list turns them into names for the timeline.
      const [d, members] = await Promise.all([
        leadsService.get(leadUid),
        teamRef.current ?? settingsService.listTeam().catch(() => [] as TeamUser[]),
      ]);
      teamRef.current = members;
      setTeam(members);
      setDetail(d);
      setLead(d.lead);
      setMessages(buildTranscript(d, members));
    } catch {
      toast.error('Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Best-effort: blue-ticks the latest inbound on Meta when this page opens.
    if (leadUid) leadsService.markRead(leadUid).catch(() => undefined);
    // eslint-disable-next-line
  }, [leadUid]);

  // Scroll transcript to bottom whenever messages update. Wrapped in rAF so
  // the layout has actually painted (otherwise scrollHeight is computed before
  // all message bubbles render and we end up scrolled near the top — that's
  // the "where is my lead?" symptom on long chats).
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const id = requestAnimationFrame(() => {
      // Two rAFs: first to flush layout, second to do the scroll after.
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    });
    return () => cancelAnimationFrame(id);
  }, [messages]);

  const handleSendReply = async () => {
    if (!leadUid || !replyBody.trim()) return;
    setSending(true);
    const optimisticId = `opt-${Date.now()}`;
    const optimistic: TranscriptMessage = {
      id: optimisticId,
      role: 'outbound',
      body: replyBody.trim(),
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setReplyBody('');
    try {
      const res = await leadsService.reply(leadUid, { body: optimistic.body });
      if (!res.ok) {
        toast.error(res.error ?? 'Failed to send — possible 24-hour window expired');
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      } else {
        await load();
      }
    } catch {
      // axios interceptor already toasted the backend message; just drop the optimistic bubble
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
    } finally {
      setSending(false);
    }
  };

  const handleSendVoice = async (blob: Blob, durationSec: number) => {
    if (!leadUid) return;
    setSendingVoice(true);
    const optimisticId = `opt-voice-${Date.now()}`;
    try {
      // 1. Upload blob to ImageKit
      const audioFile = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type || 'audio/webm' });
      const uploadResult = await uploadsService.imagekit(audioFile, `voice-notes`, 'voice,outbound');
      const audioUrl = uploadResult.url;
      const audioMime = blob.type || 'audio/webm';

      // 2. Transcribe (best-effort)
      let transcript: string | undefined;
      try {
        const transcribeResult = await voiceService.transcribe(audioFile);
        transcript = transcribeResult.text;
      } catch {
        // transcription is optional; don't block send
      }

      // 3. Optimistic bubble
      const optimistic: TranscriptMessage = {
        id: optimisticId,
        role: 'outbound',
        body: transcript,
        audioUrl,
        audioMime,
        audioDurationSec: durationSec,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
      setShowVoiceRecorder(false);

      // 4. Send via API
      const res = await leadsService.reply(leadUid, {
        audioUrl,
        audioMime,
        audioTranscript: transcript,
      });
      if (!res.ok) {
        toast.error(res.error ?? 'Failed to send voice note — possible 24-hour window expired');
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      } else {
        await load();
      }
    } catch {
      // axios interceptor already toasted the backend message; just drop the optimistic bubble
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
    } finally {
      setSendingVoice(false);
    }
  };

  const handleStatusChange = async (next: string) => {
    if (!lead || !leadUid) return;
    // Open capture dialogs for terminal statuses
    if (next === 'won') { setWonDialogOpen(true); return; }
    if (next === 'lost') { setLostDialogOpen(true); return; }
    setUpdatingStatus(true);
    try {
      const updated = await leadsService.update(leadUid, { status: next as LeadStatus });
      setLead(updated);
      toast.success(`Status updated to ${next}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkWon = async (amount: number, currency: string, reason: string) => {
    if (!leadUid) return;
    const updated = await leadsService.update(leadUid, {
      status: 'won',
      estimatedValueAmount: amount || undefined,
      estimatedValueCurrency: currency,
      closedReason: reason || undefined,
    });
    setLead(updated);
    setWonDialogOpen(false);
    toast.success('Lead marked as won!');
    await load();
  };

  const handleMarkLost = async (reason: string) => {
    if (!leadUid) return;
    const updated = await leadsService.update(leadUid, {
      status: 'lost',
      closedReason: reason || undefined,
    });
    setLead(updated);
    setLostDialogOpen(false);
    toast.success('Lead marked as lost');
    await load();
  };

  const handleAddNote = async () => {
    if (!leadUid || !noteBody.trim()) return;
    setSavingNote(true);
    try {
      await leadsService.addNote(leadUid, noteBody.trim());
      setNoteBody('');
      setNoteOpen(false);
      toast.success('Note added');
      await load();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  /**
   * Phase 5: Single-click take-over — pauses bot, assigns to caller, sets contacted.
   */
  const handleTakeOverNow = async () => {
    if (!leadUid) return;
    setTakingOver(true);
    try {
      const updated = await leadsService.takeOverNow(leadUid);
      setLead(updated);
      toast.success("You're now handling this lead");
      await load();
      // Focus reply box
      setTimeout(() => replyRef.current?.focus(), 100);
    } catch {
      toast.error('Failed to take over lead');
    } finally {
      setTakingOver(false);
    }
  };

  const handleReassign = async (uid: string) => {
    if (!leadUid) return;
    try {
      const updated = await leadsService.update(leadUid, { assignedToUserUid: uid });
      setLead(updated);
      toast.success('Lead reassigned');
    } catch {
      toast.error('Failed to reassign');
      throw new Error('Failed to reassign');
    }
  };

  const handlePauseBot = async () => {
    if (!leadUid) return;
    setBotActionLoading(true);
    try {
      const updated = await leadsService.pauseBot(leadUid);
      setLead(updated);
      toast.success('Bot paused — manual mode active');
      await load();
    } catch {
      toast.error('Failed to pause bot');
    } finally {
      setBotActionLoading(false);
    }
  };

  const handleResumeBot = async () => {
    if (!leadUid) return;
    setBotActionLoading(true);
    try {
      const updated = await leadsService.resumeBot(leadUid);
      setLead(updated);
      toast.success('Bot resumed');
      await load();
    } catch {
      toast.error('Failed to resume bot');
    } finally {
      setBotActionLoading(false);
    }
  };

  const noteEvents = detail?.leadEvents.filter((e) => e.type === 'note_added') ?? [];

  if (loading) {
    return (
      <>
        <Topbar title={t('leads', 'singular')} breadcrumb={t('leads')} />
        <div className="px-7 py-7 w-full flex gap-5">
          <div className="flex-1 space-y-3">
            <div className="skeleton h-8 w-48 rounded" />
            <div className="skeleton h-72 w-full rounded-2xl" />
          </div>
          <div className="w-80 space-y-3">
            <div className="skeleton h-40 w-full rounded-2xl" />
            <div className="skeleton h-32 w-full rounded-2xl" />
          </div>
        </div>
      </>
    );
  }

  if (!lead) {
    return (
      <>
        <Topbar title={`${t('leads', 'singular')} not found`} breadcrumb={t('leads')} />
        <div className="px-7 py-7 text-text-muted text-sm">This lead does not exist.</div>
      </>
    );
  }

  const initials = initialsFor(lead.profileName, lead.phone);
  const botActive = !!lead.botFlowUid;

  return (
    <>
      <Topbar
        title={lead.profileName || lead.phone || lead.email || 'New lead'}
        breadcrumb={t('leads')}
        back={
          <button
            type="button"
            onClick={() => navigate('/leads')}
            aria-label={`Back to ${t('leads').toLowerCase()}`}
            title={`Back to ${t('leads').toLowerCase()}`}
            className={clsx(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              'border border-border bg-surface text-text-secondary',
              'hover:border-primary hover:bg-primary-soft hover:text-primary transition-colors',
            )}
          >
            <ArrowLeft size={16} />
          </button>
        }
        right={
          <div className="flex items-center gap-2">
            {hasModule('contacts') && lead.contactUid && <Button variant="secondary" size="sm" onClick={() => navigate(`/contacts/${lead.contactUid}`)}>Customer profile</Button>}
            {/* Phase 5: Combined take-over-now button */}
            <Button
              variant="primary"
              size="sm"
              loading={takingOver}
              iconLeft={<Zap size={14} />}
              onClick={handleTakeOverNow}
            >
              Take over
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Briefcase size={14} />}
              onClick={() => setHandoverOpen(true)}
              title="Hand off to another department"
            >
              Hand off
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Trash2 size={14} />}
              onClick={() => setDeleteOpen(true)}
              title="Delete this lead"
            >
              Delete
            </Button>
          </div>
        }
      />

      <ConfirmDialog
        open={deleteOpen}
        title={`Delete this ${t('leads', 'singular').toLowerCase()}?`}
        message={
          deleting
            ? 'Deleting…'
            : `This will hide ${lead.profileName || lead.phone || lead.email || 'New lead'} from your ${t('leads').toLowerCase()} list. Chat history is kept for audit. This action can be reversed by a developer if needed.`
        }
        danger
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />

      <Modal open={handoverOpen} onClose={() => setHandoverOpen(false)} title="Hand off to department" widthClass="max-w-md">
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Reassigns this lead to another team. Users in the target department will get a notification. The current per-user assignee (if any) will be cleared so the new team picks it up fresh.
          </p>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">Department</label>
            <select
              value={handoverDeptUid}
              onChange={(e) => setHandoverDeptUid(e.target.value)}
              className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            >
              <option value="">— Select a department —</option>
              {departments
                .filter((d) => d.deptUid !== lead.assignedToDepartmentUid)
                .map((d) => (
                  <option key={d.deptUid} value={d.deptUid}>
                    {d.name}{d.isDefault ? ' (default)' : ''}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">Reason (optional)</label>
            <textarea
              rows={2}
              value={handoverReason}
              onChange={(e) => setHandoverReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Customer wants a flutter app, escalating to Mobile App Team"
              className="w-full resize-none rounded-xl border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setHandoverOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={!handoverDeptUid || handingOver} loading={handingOver} onClick={handleHandover}>
              Hand off
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assignment banner */}
      <AssignmentBanner lead={lead} onReassign={handleReassign} />

      <div className="flex flex-col gap-5 px-7 pt-3 pb-4 lg:flex-row lg:items-stretch w-full">
        {/* ── Left column: Transcript ─────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <Card
            padded={false}
            className="flex flex-col overflow-hidden"
            style={{ height: 'calc(100vh - 170px)', minHeight: 440 }}
          >
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto"
              style={{ background: 'var(--color-surface-alt, #f9fafb)' }}
            >
              {/* min-h-full + justify-end pins messages to the bottom of the
                  scroll viewport when the thread is short, so there's no dead
                  space below the last bubble. Overflow still scrolls normally
                  when the thread is long. */}
              <div className="flex min-h-full flex-col justify-end">
                <ConversationTranscript messages={messages} loading={loading} />
              </div>
            </div>

            {/* Bot status strip */}
            {botActive ? (
              <div className="border-t border-border bg-primary-soft/30 px-4 py-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-primary text-xs font-medium">
                  <Bot size={13} />
                  <span>Bot is running — replies you send will pause the bot.</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<Pause size={12} />}
                  loading={botActionLoading}
                  onClick={handlePauseBot}
                >
                  Pause bot
                </Button>
              </div>
            ) : (
              <div className="border-t border-border bg-surface-alt/50 px-4 py-2 flex items-center justify-between gap-2">
                <span className="text-xs text-text-muted">Bot paused — manual mode</span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<Play size={12} />}
                  loading={botActionLoading}
                  onClick={handleResumeBot}
                >
                  Resume bot
                </Button>
              </div>
            )}

            {/* Reply box */}
            <div className="border-t border-border bg-surface p-3">
              {showVoiceRecorder ? (
                <VoiceRecorder
                  onSend={handleSendVoice}
                  onDiscard={() => setShowVoiceRecorder(false)}
                  sending={sendingVoice}
                />
              ) : (
                <div className="flex items-end gap-2">
                  <textarea
                    ref={replyRef}
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendReply();
                      }
                    }}
                    placeholder="Type a reply… (Enter to send, Shift+Enter for newline)"
                    rows={2}
                    className={clsx(
                      'flex-1 resize-none rounded-xl border border-border bg-surface-alt px-3.5 py-2.5',
                      'text-sm text-text-primary placeholder:text-text-muted',
                      'focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
                    )}
                  />
                  {/* Mic button to open voice recorder */}
                  <button
                    type="button"
                    onClick={() => setShowVoiceRecorder(true)}
                    title="Send a voice note"
                    className={clsx(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition',
                      'border-border bg-surface-alt text-text-secondary',
                      'hover:border-primary hover:bg-primary-soft hover:text-primary',
                    )}
                  >
                    <Mic size={16} />
                  </button>
                  <Button
                    size="sm"
                    iconLeft={<Send size={14} />}
                    loading={sending}
                    disabled={!replyBody.trim()}
                    onClick={handleSendReply}
                  >
                    Send
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* ── Right column ─────────────────────────────────────────────────────── */}
        <div className="flex w-full flex-col gap-4 lg:w-80 lg:shrink-0">
          {/* Prospect card */}
          <Card padded>
            <SectionHeader title="Prospect" icon={<User size={15} />} />
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary text-sm font-bold shadow-soft">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-text-primary text-sm leading-tight truncate">
                  {lead.profileName || lead.email || 'New lead'}
                </p>
                <p className="font-mono text-xs text-text-muted">{lead.phone || lead.email || 'No contact details'}</p>
              </div>
            </div>

            <div className="space-y-2.5">
              {/* Department */}
              {(() => {
                const d = departments.find((x) => x.deptUid === lead.assignedToDepartmentUid);
                return (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-muted flex items-center gap-1">
                      <Briefcase size={11} /> Department
                    </span>
                    {d ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium"
                        style={{ borderColor: `${d.color}55`, color: d.color, backgroundColor: `${d.color}15` }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: d.color }} />
                        {d.name}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setHandoverOpen(true)}
                        className="text-xs text-text-muted underline hover:text-primary"
                      >
                        Unassigned — set
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Status — native <select> with appearance:none so the value
                  text is rendered with our color tokens. The shared <Select>
                  component renders a native chevron whose system-level
                  rendering can mute the value-text color on some browsers,
                  making "Contacted" effectively invisible against the
                  surface. */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">Status</span>
                <select
                  value={lead.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  className={clsx(
                    'w-auto h-7 rounded-md border border-border bg-surface',
                    'pl-2 pr-7 text-xs font-medium text-text-primary',
                    'focus:outline-none focus:ring-2 focus:ring-primary',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                  )}
                  style={{
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage:
                      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238A8C92' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '11px',
                  }}
                >
                  {LEAD_STATUSES.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">Current</span>
                <LeadStatusPill status={lead.status as LeadStatus} size="sm" />
              </div>

              {/* One-click lead → deal conversion */}
              <div className="pt-1">
                {existingDeal ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    iconLeft={<Banknote size={14} />}
                    onClick={() => navigate('/deals')}
                    title={`A deal already exists for this lead: ${existingDeal.title}`}
                  >
                    View deal
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    iconLeft={<Banknote size={14} />}
                    onClick={() => setDealDialogOpen(true)}
                  >
                    Create deal
                  </Button>
                )}
              </div>

              {/* Follow-up reminder */}
              <div className="pt-1">
                <span className="mb-1 flex items-center gap-1 text-xs text-text-muted">
                  <AlarmClock size={11} /> Follow-up
                  {lead.nextFollowUpAt
                    && ['new', 'contacted', 'qualified'].includes(lead.status)
                    && new Date(lead.nextFollowUpAt).getTime() < Date.now() && (
                    <span className="font-semibold text-danger">· overdue</span>
                  )}
                </span>
                <DateTimePicker
                  value={followUpDraft}
                  onChange={setFollowUpDraft}
                  disabled={savingFollowUp}
                  placeholder="Schedule a follow-up"
                />
                {followUpDirty && (
                  <div className="mt-1.5 flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFollowUpDraft(toLocalDateTimeValue(lead.nextFollowUpAt))}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" loading={savingFollowUp} onClick={handleFollowUpSave}>
                      {followUpDraft ? 'Save follow-up' : 'Clear follow-up'}
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted flex items-center gap-1">
                  <Clock size={11} /> Created
                </span>
                <span className="text-xs text-text-secondary">{relativeTime(lead.createdAt)}</span>
              </div>

              {lead.assignedToUserUid && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-muted flex items-center gap-1">
                    <UserCheck size={11} /> Assigned
                  </span>
                  {(() => {
                    const member = team.find((u) => u.userUid === lead.assignedToUserUid);
                    return member
                      ? <span className="text-xs text-text-secondary truncate max-w-[140px]">{memberName(member)}</span>
                      : <span className="text-xs font-mono text-text-secondary truncate max-w-[110px]">{lead.assignedToUserUid}</span>;
                  })()}
                </div>
              )}



              {lead.estimatedValueAmount != null && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-muted">Est. value</span>
                  <span className="text-xs font-semibold text-text-primary">
                    {lead.estimatedValueCurrency ?? ''} {lead.estimatedValueAmount.toLocaleString()}
                  </span>
                </div>
              )}

              {lead.botFlowUid && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-muted flex items-center gap-1">
                    <Bot size={11} /> Flow
                  </span>
                  <Link
                    to={`/leads/bot-flows/${lead.botFlowUid}`}
                    className="rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10 transition"
                  >
                    {lead.botFlowUid}
                  </Link>
                </div>
              )}
            </div>
          </Card>

          {/* Form Answers */}
          {lead.source === 'lead_ad_form' && Object.keys(lead.collectedFields ?? {}).length > 0 && (
            <Card padded>
              <SectionHeader title="Form Answers" icon={<ClipboardList size={15} />} />
              <CollectedFieldsTable fields={lead.collectedFields} />
            </Card>
          )}

          {/* Provenance */}
          <LeadProvenanceCard lead={lead} />

          {/* Custom object records linked to this lead */}
          <LeadCustomRecords leadUid={leadUid!} />

          {/* Collected fields */}
          {lead.source !== 'lead_ad_form' && Object.keys(lead.collectedFields ?? {}).length > 0 && (
            <Card padded>
              <SectionHeader title="Collected fields" />
              <CollectedFieldsTable fields={lead.collectedFields} />
            </Card>
          )}

            {/* Won/Lost details */}
          {(lead.status === 'won' || lead.status === 'lost') && (
            <Card padded>
              <SectionHeader
                title={lead.status === 'won' ? 'Deal details' : 'Lost details'}
                icon={lead.status === 'won' ? <Trophy size={15} className="text-success" /> : <AlertCircle size={15} className="text-danger" />}
              />
              <div className="space-y-2.5">
                {lead.estimatedValueAmount != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-muted">Deal value</span>
                    <span className="text-sm font-bold text-success tabular-nums">
                      {lead.estimatedValueCurrency ?? companyCurrency} {lead.estimatedValueAmount.toLocaleString()}
                    </span>
                  </div>
                )}
                {lead.closedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-muted">Closed</span>
                    <span className="text-xs text-text-secondary">{relativeTime(lead.closedAt)}</span>
                  </div>
                )}
                {lead.closedReason && (
                  <div>
                    <span className="text-xs text-text-muted block mb-1">Reason</span>
                    <p className="text-xs text-text-primary bg-surface-alt rounded-lg px-3 py-2">
                      {lead.closedReason}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Notes */}
          <Card padded>
            <SectionHeader
              title="Notes"
              icon={<StickyNote size={15} />}
              action={
                <button
                  className="text-xs font-semibold text-primary hover:underline"
                  onClick={() => setNoteOpen((v) => !v)}
                >
                  + Add note
                </button>
              }
            />

            {noteOpen && (
              <div className="mb-3 space-y-2">
                <textarea
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  placeholder="Add a note about this lead…"
                  rows={3}
                  className={clsx(
                    'w-full resize-none rounded-xl border border-border bg-surface-alt px-3 py-2.5',
                    'text-sm text-text-primary placeholder:text-text-muted',
                    'focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
                  )}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setNoteOpen(false); setNoteBody(''); }}>
                    Cancel
                  </Button>
                  <Button size="sm" loading={savingNote} disabled={!noteBody.trim()} onClick={handleAddNote}>
                    Save note
                  </Button>
                </div>
              </div>
            )}

            {noteEvents.length === 0 && !noteOpen && (
              <p className="text-xs text-text-muted italic">No notes yet.</p>
            )}

            <div>
              {noteEvents.map((ev) => (
                <NoteRow key={ev.eventUid} event={ev} />
              ))}
            </div>
          </Card>

          {/* Activity timeline (polymorphic — deals, tasks, notes, system) */}
          <Card padded>
            <SectionHeader title="Activity" icon={<ClipboardList size={15} />} />
            <ActivityTimeline entityType="lead" entityUid={leadUid!} limit={30} />
          </Card>
        </div>
      </div>

      {/* Mark-as-won dialog */}
      <WonDialog
        open={wonDialogOpen}
        onClose={() => setWonDialogOpen(false)}
        onConfirm={handleMarkWon}
        defaultCurrency={lead.estimatedValueCurrency ?? companyCurrency}
      />

      {/* Mark-as-lost dialog */}
      <LostDialog
        open={lostDialogOpen}
        onClose={() => setLostDialogOpen(false)}
        onConfirm={handleMarkLost}
      />

      {/* Create-deal dialog */}
      <CreateDealDialog
        open={dealDialogOpen}
        onClose={() => setDealDialogOpen(false)}
        lead={lead}
        onCreated={(deal) => { setExistingDeal(deal); load(); }}
      />
    </>
  );
}
